import { useCallback, useEffect, useState } from 'react';

/**
 * Shared cursor state for the execution detail views (Executions page and the runbook
 * execution detail): one reference time drives the vertical cursor on *every* metrics
 * chart at once.
 *
 * Two things can move the cursor:
 *  - hovering an execution log row (transient), and
 *  - locking it, either by clicking a log row or by clicking a point on any chart.
 *
 * A lock wins over hover and survives until it is released — by clicking the same chart
 * point again, by selecting something else, or by clicking anywhere outside the charts
 * and the log table.
 */
export interface ExecutionCursorSync {
  /** Reference time (ms) the charts should render, or null for no cursor. */
  activeReferenceMs: number | null;
  /** Index of the locked execution log row, or null when no row is locked. */
  selectedLogRowIndex: number | null;
  /** True while the cursor is pinned (log row or chart point). */
  isLocked: boolean;
  /** Hover an execution log row; ignored visually while the cursor is locked. */
  hoverLogRow: (timeMs: number | null) => void;
  /** Lock the cursor on an execution log row. */
  selectLogRow: (index: number, timeMs: number) => void;
  /** Lock the cursor on a clicked chart point, or release it when already locked there. */
  selectChartPoint: (timeMs: number | null) => void;
  /** Release the lock and any hover state. */
  clear: () => void;
}

export function useExecutionCursorSync(): ExecutionCursorSync {
  const [hoverMs, setHoverMs] = useState<number | null>(null);
  const [selectedLogRowIndex, setSelectedLogRowIndex] = useState<number | null>(null);
  const [lockedMs, setLockedMs] = useState<number | null>(null);

  const clear = useCallback(() => {
    setHoverMs(null);
    setSelectedLogRowIndex(null);
    setLockedMs(null);
  }, []);

  // Click anywhere that is not a chart or a log row releases the lock. Both of those
  // stop the click, so this only sees "clicked somewhere else on the page".
  useEffect(() => {
    if (lockedMs === null) return;
    document.addEventListener('click', clear);
    return () => document.removeEventListener('click', clear);
  }, [lockedMs, clear]);

  const hoverLogRow = useCallback((timeMs: number | null) => {
    setHoverMs(timeMs);
  }, []);

  const selectLogRow = useCallback((index: number, timeMs: number) => {
    setSelectedLogRowIndex(index);
    setLockedMs(timeMs);
    setHoverMs(timeMs);
  }, []);

  const selectChartPoint = useCallback((timeMs: number | null) => {
    if (timeMs === null) return;
    setSelectedLogRowIndex(null);
    setHoverMs(null);
    setLockedMs((prev) => (prev === timeMs ? null : timeMs));
  }, []);

  return {
    activeReferenceMs: lockedMs ?? hoverMs,
    selectedLogRowIndex,
    isLocked: lockedMs !== null,
    hoverLogRow,
    selectLogRow,
    selectChartPoint,
    clear,
  };
}
